import type {
  PaymentObservationInput,
  PaymentObservationResponse,
  StartPaymentAttemptResponse,
} from '../api/paymentAttempts';
import type {
  ZettleAvailability,
  ZettleBridgePayload,
  ZettleReaderPermissionStatus,
  ZettleSessionIdentity,
} from '../native/zettlePayments';

/**
 * PayPal/Zettle Entrega 1: decisiones PURAS del flujo de cobro con lector en el POS.
 *
 * Viven aquí (probables en la JVM del navegador, sin componente) para que la regla más cara no
 * dependa de un render: **un intento reutilizado JAMÁS relanza el SDK** — si ese intento ya cobró,
 * relanzarlo cobraría dos veces. Lo reutilizado se recupera (reportar honestamente "no sé" y
 * verificar por la misma referencia), nunca se repite.
 */

export type ZettleStep =
  /** Intento recién entregado: lanzar la UI del SDK con esta referencia. */
  | 'launch'
  /** Intento en vuelo de una sesión anterior (proceso muerto): reportar UNKNOWN y verificar. */
  | 'recover'
  /** Intento ya en duda: ir directo a verificar por la misma referencia. */
  | 'reconcile'
  /** El intento ya tiene desenlace: refrescar la venta, no lanzar nada. */
  | 'refresh';

/**
 * Aviso del cobro con lector. `action: 'login'` ofrece abrir el inicio de sesión del SDK (solo REAL);
 * `'app-settings'` (P-015) ofrece abrir los Ajustes de la app para conceder un permiso a mano.
 */
export interface ZettleNotice {
  tone: 'success' | 'info' | 'error' | 'warning';
  title: string;
  detail: string;
  action?: 'login' | 'app-settings';
  /**
   * P-015: de qué habla el aviso, para poder reemplazarlo cuando deja de ser cierto.
   *
   * Deuda (l): los avisos de permisos no lo tenían, así que NADA en la app podía corregirlos y el
   * aviso rojo seguía en pantalla después de conceder el permiso en los Ajustes de Android.
   */
  topic?: 'zettle-login' | 'zettle-reader-permissions';
  /**
   * El SERVIDOR dijo que esta venta tiene un cobro con tarjeta SIN CERRAR. Viaja dentro del aviso
   * para que quien lo reemplace no pueda borrar el hecho sin enterarse: un permiso concedido o una
   * sesión iniciada no cambian que ese cobro sigue sin resolverse.
   */
  cardPending?: true;
}

export function decideZettleStep(start: Pick<StartPaymentAttemptResponse, 'reused' | 'status'>): ZettleStep {
  if (!start.reused) return 'launch';
  // Reutilizado = ya existía. NUNCA se relanza el SDK: el lector pudo haber cobrado.
  if (start.status === 'UNKNOWN') return 'reconcile';
  if (start.status === 'SUBMITTED' || start.status === 'PROCESSING') return 'recover';
  return 'refresh';
}

/**
 * Traduce lo que cruzó el puente al cuerpo de la observación para el backend.
 * Solo campos del contrato; `detail` (texto libre del gateway) NO viaja al servidor.
 *
 * Moneda: el servidor exige `observedCurrency` en todo COMPLETED (fix H1) y la verifica contra el
 * cobro. El payload del RETRIEVE la trae del SDK; el del RESULTADO DEL COBRO no la expone (AAR
 * 2.52.1), así que para ese canal el POS reporta la moneda con la que el SERVIDOR entregó el
 * intento (`fallbackCurrency`) — la moneda en la que se instruyó cobrar. La del SDK, cuando existe,
 * siempre gana: es la observación real.
 */
export function observationFromBridge(
  payload: ZettleBridgePayload,
  source: PaymentObservationInput['source'],
  options: { fallbackCurrency?: string } = {},
): PaymentObservationInput {
  const currency = payload.currency ?? options.fallbackCurrency;
  return {
    source,
    outcome: payload.outcome,
    ...(payload.transactionId ? { providerTransactionId: payload.transactionId } : {}),
    ...(payload.reason ? { rawReason: payload.reason } : {}),
    ...(typeof payload.amountMinor === 'number' ? { observedAmountMinor: payload.amountMinor } : {}),
    ...(currency ? { observedCurrency: currency } : {}),
  };
}

/** El reporte honesto cuando la app volvió a abrir con un cobro en vuelo: no sabemos, y se dice. */
export const RECOVERY_OBSERVATION: PaymentObservationInput = {
  source: 'RECOVERY',
  outcome: 'UNKNOWN',
  rawReason: 'ProcessDeath',
};

/** Qué debe ver la persona según el desenlace que confirmó el SERVIDOR (nunca jerga técnica). */
export function describeZettleOutcome(
  response: Pick<PaymentObservationResponse, 'attemptStatus'>,
  options: { watching?: boolean } = {},
): ZettleNotice {
  switch (response.attemptStatus) {
    case 'SUCCEEDED':
      return { tone: 'success', title: 'Cobro con lector registrado', detail: 'La venta quedó completada.' };
    case 'CANCELED':
      return { tone: 'info', title: 'Cobro cancelado en el lector', detail: 'No se registró ningún cobro. Puedes intentar de nuevo o elegir otra forma de pago.' };
    case 'FAILED':
      return { tone: 'error', title: 'El cobro fue rechazado', detail: 'Intenta de nuevo o elige otra forma de pago.' };
    default:
      // Deuda (t), segunda vuelta. En el equipo, el 2026-09-30: el lector dijo «Pago aceptado», el
      // servidor respondió en 0.2 s con UNKNOWN —que es su diseño, no le cree al teléfono— y la
      // pantalla se quedó **20.67 segundos** con «No sabemos si el cobro se realizó», inmóvil hasta
      // el byte, hasta que la reconciliación confirmó sola. Medido fotograma a fotograma.
      //
      // Ese texto es correcto pero es una ALARMA, y durante esos 20 s nada decía que el sistema
      // estuviera trabajando. Con un cliente enfrente, 20 segundos de pantalla muerta con la palabra
      // «No sabemos» es exactamente el momento en que alguien vuelve a cobrar.
      //
      // Mientras el vigilante esté preguntando al servidor, el estado es «estamos confirmando». La
      // alarma se reserva para cuando la espera de verdad se agote (`READER_SALE_STILL_IN_DOUBT_NOTICE`).
      if (options.watching) {
        return {
          tone: 'info',
          title: 'Confirmando el cobro con el servidor…',
          detail: 'El lector terminó. Estamos confirmando con el banco si el cobro pasó; suele tardar unos segundos. No vuelvas a cobrar ni cobres de otra forma: la pantalla se actualiza sola.',
        };
      }
      return {
        tone: 'warning',
        title: 'No sabemos si el cobro se realizó',
        detail: 'No vuelvas a cobrar todavía. Usa "Verificar cobro" para confirmar qué pasó.',
      };
  }
}

// ─── PAYPAL-GATE-02 · build REAL (piloto) ─────────────────────────────────────────────────────────

const ZETTLE_UNAVAILABLE: ZettleAvailability = { available: false, devMode: false, mode: 'NONE' };

/**
 * Lo que respondió el puente, normalizado. El modo lo decide el BUILD de Android; aquí solo se
 * interpreta, y ante cualquier duda gana "no disponible":
 *  · un APK anterior (Entrega 1) no reporta `mode`: disponible ⇒ Developer Mode, como entonces;
 *  · una respuesta contradictoria (REAL con devMode, DEV sin devMode) no se adivina;
 *  · jamás se deduce REAL de datos ausentes.
 */
export function normalizeZettleAvailability(raw: Partial<ZettleAvailability> | null | undefined): ZettleAvailability {
  if (!raw || raw.available !== true) return ZETTLE_UNAVAILABLE;
  const mode = raw.mode ?? (raw.devMode === true ? 'DEV' : undefined);
  if (mode === 'DEV' && raw.devMode === true) return { available: true, devMode: true, mode: 'DEV' };
  if (mode === 'REAL' && raw.devMode === false) return { available: true, devMode: false, mode: 'REAL', loggedIn: raw.loggedIn === true };
  return ZETTLE_UNAVAILABLE;
}

/** Cualquier respuesta que no sea exactamente GRANTED o DENIED_PERMANENTLY cuenta como negada. */
export function normalizeReaderPermissionStatus(raw: unknown): ZettleReaderPermissionStatus {
  return raw === 'GRANTED' || raw === 'DENIED_PERMANENTLY' ? raw : 'DENIED';
}

const ZETTLE_SESSION_UNKNOWN: ZettleSessionIdentity = { mode: 'NONE', loggedIn: false };

/**
 * Deuda (n): lo que el puente reporta sobre la sesión del SDK, normalizado. Nada se adivina: un campo
 * que no tiene exactamente la forma esperada se OMITE, porque un sufijo a medias compararía mal y un
 * código de moneda inventado podría bloquear un cobro legítimo.
 */
export function normalizeZettleSessionIdentity(
  raw: Partial<ZettleSessionIdentity> | null | undefined,
): ZettleSessionIdentity {
  if (!raw) return ZETTLE_SESSION_UNKNOWN;
  const mode = raw.mode === 'DEV' || raw.mode === 'REAL' || raw.mode === 'NONE' ? raw.mode : 'NONE';
  const identity: ZettleSessionIdentity = { mode, loggedIn: raw.loggedIn === true };
  if (!identity.loggedIn) return identity;
  const organization = typeof raw.organizationSuffix === 'string' ? raw.organizationSuffix.toLowerCase() : '';
  if (/^[0-9a-f]{8}$/.test(organization)) identity.organizationSuffix = organization;
  const user = typeof raw.userSuffix === 'string' ? raw.userSuffix.toLowerCase() : '';
  if (/^[0-9a-f]{8}$/.test(user)) identity.userSuffix = user;
  const country = typeof raw.country === 'string' ? raw.country.toUpperCase() : '';
  if (/^[A-Z]{2}$/.test(country)) identity.country = country;
  const currency = typeof raw.currency === 'string' ? raw.currency.toUpperCase() : '';
  if (/^[A-Z]{3}$/.test(currency)) identity.currency = currency;
  return identity;
}

/**
 * Deuda (n): la línea que el dueño lee en «Conexión Zettle» para saber con qué cuenta de Zettle está
 * el equipo que tiene en la mano. Es un DIAGNÓSTICO, no una defensa: se calcula en el navegador
 * comparando dos sufijos, y quien impide de verdad un cobro en la cuenta equivocada es el servidor.
 *
 * Informa la sesión del EQUIPO donde está abierta la pantalla, así que para ver la del SP01 hay que
 * abrirla en el SP01.
 */
export function describeZettleDeviceSession(
  expectedExternalAccountId: string | null,
  session: ZettleSessionIdentity | undefined,
): { tone: 'ok' | 'warn' | 'neutral'; text: string } {
  if (!session || session.mode === 'NONE') {
    return {
      tone: 'neutral',
      text: 'Este equipo no tiene el lector. Abre esta pantalla dentro de la app PILOTO, en el equipo donde está el lector.',
    };
  }
  if (session.mode === 'DEV') {
    return { tone: 'neutral', text: 'Modo de desarrollo: sin sesión real y sin dinero real.' };
  }
  if (!session.loggedIn) {
    // No se afirma «no hay sesión»: el estado del SDK puede no haber emitido todavía (app recién
    // abierta), y no sabemos distinguirlo. UNKNOWN no es FAILED, tampoco en un diagnóstico.
    return {
      tone: 'neutral',
      text: 'Este equipo no reporta una sesión de Zettle. Si acabas de abrir la app, espera un momento y vuelve a entrar a esta pantalla.',
    };
  }
  const place = [session.country, session.currency].filter(Boolean).join(' · ');
  const detail = place ? ` (${place})` : '';
  if (!session.organizationSuffix) {
    return {
      tone: 'warn',
      text: `Hay sesión de Zettle en este equipo${detail}, pero no se pudo leer la organización, así que no se puede comparar con la cuenta conectada.`,
    };
  }
  // Developer Mode guarda un marcador, no una organización real: comparar contra él daría un
  // «no coinciden» inventado.
  const isDeveloperAccount = expectedExternalAccountId === 'devmode-local';
  const expected = isDeveloperAccount ? undefined : expectedExternalAccountId?.slice(-8).toLowerCase();
  if (!expected) {
    return {
      tone: 'neutral',
      text: `Este equipo está en la organización …${session.organizationSuffix}${detail}. El negocio no tiene ninguna cuenta real de Zettle conectada con la que comparar.`,
    };
  }
  if (expected !== session.organizationSuffix) {
    // Deliberadamente NO se ordena borrar los datos de la app: eso pierde el emparejamiento del
    // lector, la sesión de Negocio360 y las marcas locales de las ventas en duda. Además, que el
    // identificador del SDK y el del OAuth del servidor sean el mismo valor es una INFERENCIA que
    // el repo no ha verificado: un «no coinciden» puede ser de formato, no de cuenta.
    return {
      tone: 'warn',
      text: `No coinciden: este equipo reporta la organización …${session.organizationSuffix}${detail} y el negocio tiene conectada la …${expected}. Puede ser que el equipo esté en otra cuenta de Zettle. No borres los datos de la app todavía: revisa antes con qué cuenta se inició sesión.`,
    };
  }
  const user = session.userSuffix ? ` · Usuario …${session.userSuffix}` : '';
  return {
    tone: 'ok',
    text: `Coincide con la cuenta conectada · Organización …${session.organizationSuffix}${user}${place ? ` · ${place}` : ''}`,
  };
}

/**
 * PAYPAL-CURRENCY-01: lo que este equipo puede DECIR de su propia sesión del lector al pedir un
 * intento. El servidor decide; esto solo informa, y solo puede provocar un rechazo.
 *
 * Solo en el build REAL y solo con sesión: en Developer Mode la organización es un marcador y
 * compararla no significaría nada. Si no hay nada que decir devuelve `undefined`, y entonces el
 * servidor NO bloquea — fallar abierto, porque no saber no es estar mal.
 */
export function zettleSessionAttestation(
  session: ZettleSessionIdentity,
): { currency?: string; organizationSuffix?: string } | undefined {
  if (session.mode !== 'REAL' || !session.loggedIn) return undefined;
  const attestation: { currency?: string; organizationSuffix?: string } = {};
  if (session.currency) attestation.currency = session.currency;
  if (session.organizationSuffix) attestation.organizationSuffix = session.organizationSuffix;
  return attestation.currency || attestation.organizationSuffix ? attestation : undefined;
}

/** Aviso del rechazo por sesión en otra cuenta. El detalle lo redacta el SERVIDOR. */
export function describeZettleDeviceSessionBlock(detail: string): ZettleNotice {
  return { tone: 'warning', title: 'El lector de este equipo está en otra cuenta', detail };
}

/** Leyenda de la opción "Cobrar con lector Zettle": el build REAL (piloto) se nota a simple vista. */
export function describeZettleMethodHint(availability: Pick<ZettleAvailability, 'mode'>): { text: string; live: boolean } {
  return availability.mode === 'REAL'
    ? { text: 'Zettle · PILOTO · cobra dinero real', live: true }
    : { text: 'Zettle · modo de desarrollo, sin dinero real', live: false };
}

export type ZettlePreflightBlock = 'UNAVAILABLE' | 'LOGIN_REQUIRED' | 'PERMISSIONS_DENIED' | 'PERMISSIONS_DENIED_PERMANENTLY';

export type ZettlePreflightDecision =
  | { step: 'charge' }
  | { step: 'ask-permissions' }
  | { step: 'blocked'; reason: ZettlePreflightBlock };

/**
 * ¿Se puede pedir un intento de cobro al servidor? Se decide ANTES de crearlo, para que una sesión
 * faltante o un permiso negado no dejen un intento en vuelo que luego haya que reconciliar.
 * Developer Mode no cambia: pasa directo, sin permisos ni sesión (el simulador no los usa).
 * Esto NO autoriza cobros: el servidor sigue decidiendo si la cuenta puede cobrar (candado LIVE).
 */
export function decideZettlePreflight(availability: ZettleAvailability, permission?: ZettleReaderPermissionStatus): ZettlePreflightDecision {
  if (!availability.available || availability.mode === 'NONE') return { step: 'blocked', reason: 'UNAVAILABLE' };
  if (availability.mode === 'DEV') return { step: 'charge' };
  if (availability.loggedIn !== true) return { step: 'blocked', reason: 'LOGIN_REQUIRED' };
  if (permission === undefined) return { step: 'ask-permissions' };
  if (permission === 'GRANTED') return { step: 'charge' };
  return { step: 'blocked', reason: permission === 'DENIED_PERMANENTLY' ? 'PERMISSIONS_DENIED_PERMANENTLY' : 'PERMISSIONS_DENIED' };
}

export interface ZettlePreflightBridge {
  refreshAvailability: () => Promise<ZettleAvailability>;
  ensurePermissions: () => Promise<ZettleReaderPermissionStatus>;
}

/**
 * Orquesta el preflight con el puente inyectado (probable sin Android). En REAL la sesión se vuelve
 * a consultar (el operador pudo acabar de iniciarla) y los permisos se piden solo con sesión.
 */
export async function runZettlePreflight(
  current: ZettleAvailability,
  bridge: ZettlePreflightBridge,
): Promise<{ availability: ZettleAvailability; decision: ZettlePreflightDecision }> {
  const availability = current.mode === 'REAL' ? await bridge.refreshAvailability() : current;
  let decision = decideZettlePreflight(availability);
  if (decision.step === 'ask-permissions') decision = decideZettlePreflight(availability, await bridge.ensurePermissions());
  return { availability, decision };
}

export type ZettleAttemptStart<T> =
  /** `blocked` (P-017): había marca local, el preflight bloqueó y AUN ASÍ se pidió el intento para recuperar; el lector no debe abrirse. */
  | { kind: 'started'; availability: ZettleAvailability; start: T; blocked?: ZettlePreflightBlock }
  | { kind: 'blocked'; availability: ZettleAvailability; reason: ZettlePreflightBlock };

/**
 * ¿La venta (tal como la entregó el servidor) tiene un cobro con lector con un intento EN VILO? El servidor
 * pasa el cobro a PROCESSING cuando resuelve una observación que deja el intento en duda
 * (`derivePaymentStatus`: SUBMITTED / PROCESSING / UNKNOWN). Ahí lo que toca es RECUPERAR por la misma
 * referencia, no hacer preflight.
 *
 * P-017: PENDING ya NO cuenta. El servidor deja el cobro en PENDING tanto al nacer como cuando TODOS sus
 * intentos terminaron sin éxito (cancelado, rechazado): en el video del 2026-09-28 una venta con un intento
 * cancelado saltó el preflight en cada toque y abrió el SDK cuatro veces sin sesión ni permiso comprobados,
 * sin mostrar nunca los avisos propios. El caso legítimo que PENDING cubría —la app murió a mitad del cobro
 * ANTES de reportar, con el intento aún SUBMITTED— lo cubre ahora la marca local [rememberZettleLaunch].
 */
export function hasReaderPaymentInFlight(payments: ReadonlyArray<{ status: string }> | undefined): boolean {
  return (payments ?? []).some((payment) => payment.status === 'PROCESSING');
}

// ─── P-017 · marca local «abrí el lector para esta venta» ────────────────────────────────────────

/** Lo mínimo que se usa del almacenamiento del dispositivo (inyectable para probarlo sin navegador). */
export interface ZettleLaunchMemory {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Una marca más vieja que esto no vale: una venta no queda abierta tanto tiempo con un cobro a medias. */
export const ZETTLE_LAUNCH_MAX_AGE_MS = 24 * 60 * 60 * 1_000;

const zettleLaunchKey = (saleId: string) => `n360.zettle.launch.${saleId}`;

/** localStorage del WebView, tolerante: si no existe o falla, la marca simplemente no se guarda ni se lee. */
const deviceMemory: ZettleLaunchMemory = {
  getItem: (key) => { try { return globalThis.localStorage?.getItem(key) ?? null; } catch { return null; } },
  setItem: (key, value) => { try { globalThis.localStorage?.setItem(key, value); } catch { /* sin memoria: sin marca */ } },
  removeItem: (key) => { try { globalThis.localStorage?.removeItem(key); } catch { /* nada que borrar */ } },
};

/** Un reloj que retrocede un poco (reinicio del equipo) no invalida una marca reciente. */
export const ZETTLE_LAUNCH_CLOCK_TOLERANCE_MS = 5 * 60 * 1_000;

/**
 * Se guarda JUSTO ANTES de abrir la UI del SDK con un intento. Si el proceso muere antes de reportar el
 * resultado, al volver la marca dice «esta venta tiene un cobro con lector a medias»: se recupera por la
 * misma referencia sin depender de la sesión del SDK ni de los permisos. Solo guarda identificadores.
 * Cualquier memoria que falle (almacenamiento bloqueado, cuota) se tolera: sin marca no hay recuperación
 * local, pero el cobro sigue funcionando y el servidor sigue siendo la verdad.
 */
export function rememberZettleLaunch(saleId: string, attemptId: string, memory: ZettleLaunchMemory = deviceMemory, now: number = Date.now()): void {
  try { memory.setItem(zettleLaunchKey(saleId), JSON.stringify({ attemptId, at: now })); } catch { /* sin memoria: sin marca */ }
}

/** Se borra en cuanto el servidor recibió el resultado (cualquiera) o la venta se cerró o canceló. */
export function forgetZettleLaunch(saleId: string, memory: ZettleLaunchMemory = deviceMemory): void {
  try { memory.removeItem(zettleLaunchKey(saleId)); } catch { /* nada que borrar */ }
}

/**
 * El intento con el que se abrió el lector para esta venta, si la marca existe y es reciente. Una marca
 * vencida o corrupta se borra al leerla (no se acumulan llaves en el equipo).
 */
export function readZettleLaunch(saleId: string, memory: ZettleLaunchMemory = deviceMemory, now: number = Date.now()): string | undefined {
  let raw: string | null;
  try { raw = memory.getItem(zettleLaunchKey(saleId)); } catch { return undefined; }
  if (!raw) return undefined;
  let parsed: { attemptId?: unknown; at?: unknown } | undefined;
  try { parsed = JSON.parse(raw) as { attemptId?: unknown; at?: unknown }; } catch { parsed = undefined; }
  const valid = parsed !== undefined
    && typeof parsed.attemptId === 'string'
    && typeof parsed.at === 'number'
    && now - parsed.at <= ZETTLE_LAUNCH_MAX_AGE_MS
    && now >= parsed.at - ZETTLE_LAUNCH_CLOCK_TOLERANCE_MS;
  if (!valid) { forgetZettleLaunch(saleId, memory); return undefined; }
  return parsed!.attemptId as string;
}

/**
 * ¿La venta tiene un cobro con TARJETA que no terminó (PENDING o PROCESSING)? No dice si hay un intento en
 * vuelo (eso es [hasReaderPaymentInFlight]); sirve para que el aviso de un preflight bloqueado hable claro.
 */
export function hasUnsettledCardPayment(payments: ReadonlyArray<{ status: string; method?: string }> | undefined): boolean {
  return (payments ?? []).some((payment) => (payment.status === 'PENDING' || payment.status === 'PROCESSING') && (payment.method === undefined || payment.method === 'CARD'));
}

/**
 * Deuda (q): la ÚNICA pregunta que la pantalla necesita hacerle al servidor antes de pintar botones.
 *
 * `readReaderSaleTruth` (tres valores) no distingue «el cobro con tarjeta quedó sin resolver» de «esta
 * venta nunca tocó el lector», y el booleano que usaba la pantalla aplastaba las dos cosas. La
 * diferencia decide si se le puede ofrecer a la persona cobrar en efectivo.
 *
 * El orden de las reglas ES la regla:
 *  · confirmed       — el servidor ya completó la venta; no hay nada que cobrar;
 *  · closed          — cancelada o anulada; tampoco;
 *  · paid-not-completed — FI-53: la venta sigue por cobrar y el servidor YA confirmó su cobro. El
 *                      cobro y la venta se confirman en dos pasos, y el segundo no terminó (faltaron
 *                      existencias, el servidor se cayó en medio…). No se cobra de nuevo ni se
 *                      cancela: se completa. Va antes que todo lo demás porque un cobro confirmado
 *                      es definitivo, pase lo que pase con los otros cobros de la venta;
 *  · in-doubt        — hay un intento EN VILO (cobro en PROCESSING): no se toca nada;
 *  · card-unresolved — hubo un cobro con TARJETA que no acabó en éxito. Puede esconder dinero cobrado
 *                      (deuda o), así que NO se ofrece cobrar de otra forma;
 *  · clean           — esta venta nunca tocó el lector: flujo normal.
 *
 * `card-unresolved` también se enciende cuando el lector NUNCA llegó a abrirse (preflight bloqueado:
 * el servidor ya había creado la fila del cobro y el intento se cerró en el acto). Es un falso positivo
 * CONSERVADOR —avisa de más, nunca de menos— y hace falta un campo derivado del servidor para
 * distinguirlo; queda anotado como deuda.
 */
export type SaleCardTruth = 'confirmed' | 'closed' | 'paid-not-completed' | 'in-doubt' | 'card-unresolved' | 'clean';

/**
 * FI-53: ¿la venta trae un cobro que el SERVIDOR ya confirmó? En una venta que sigue por cobrar solo
 * puede ser un cobro con lector: el cobro manual nace confirmado junto con la venta ya completada.
 */
export function hasConfirmedPayment(payments: ReadonlyArray<{ status: string }> | undefined): boolean {
  return (payments ?? []).some((payment) => payment.status === 'SUCCEEDED');
}

export function classifySaleCardTruth(sale: {
  status: string;
  payments?: ReadonlyArray<{ status: string; method?: string }>;
}): SaleCardTruth {
  if (sale.status === 'CONFIRMED') return 'confirmed';
  if (sale.status === 'CANCELED' || sale.status === 'VOIDED') return 'closed';
  if (hasConfirmedPayment(sale.payments)) return 'paid-not-completed';
  if (hasReaderPaymentInFlight(sale.payments)) return 'in-doubt';
  if (hasUnsettledCardPayment(sale.payments)) return 'card-unresolved';
  return 'clean';
}

/**
 * Deuda (r): el banner de ventas recuperables afirmaba «no se ha cobrado nada» sobre TODAS las ventas
 * guardadas, incluidas las que ya intentaron cobrarse con el lector y pueden tener dinero cobrado.
 * Era la frase que autorizaba a la empleada a cancelarlas o a cobrarlas otra vez — y es exactamente la
 * que leyó bajo la venta del «£10.00» el 2026-09-29.
 *
 * No necesita ninguna llamada nueva: la lista de pendientes ya trae los cobros con su método y estado.
 */
export function describeRecoverableCardRisk(
  sales: ReadonlyArray<{ id: string; payments?: ReadonlyArray<{ status: string; method?: string }> }>,
): { detail: string; atRisk: ReadonlySet<string>; flag: string; paid: ReadonlySet<string>; paidFlag: string } {
  // FI-53: una venta con el cobro YA confirmado no es «una que intentó cobrarse»: está cobrada y
  // falta completarla. Lleva su propia marca, y ninguna frase puede decir de ella que no se registró
  // ningún cobro, ni invitar a cancelarla o a cobrarla otra vez.
  const paid = new Set(sales.filter((sale) => hasConfirmedPayment(sale.payments)).map((sale) => sale.id));
  const atRisk = new Set(sales.filter((sale) => !paid.has(sale.id) && hasUnsettledCardPayment(sale.payments)).map((sale) => sale.id));
  const flag = 'Intentó cobrarse con el lector';
  const paidFlag = 'Cobro confirmado · falta completarla';
  if (paid.size > 0) {
    const unmarked = sales.length - paid.size - atRisk.size;
    const detail = [
      sales.length === 1
        ? 'Esta venta ya tiene el cobro con tarjeta confirmado y falta completarla: ábrela con «Continuar». No la cobres otra vez.'
        : paid.size === 1
          ? 'Una de estas ventas ya tiene el cobro con tarjeta confirmado y falta completarla: ábrela con «Continuar». No la cobres otra vez.'
          : 'Algunas de estas ventas ya tienen el cobro con tarjeta confirmado y falta completarlas: ábrelas con «Continuar», una por una. No las cobres otra vez.',
      atRisk.size > 0 ? `Las marcadas «${flag}» pueden tener dinero cobrado: ábrelas con «Continuar» antes de cobrarlas otra vez o cancelarlas.` : '',
      unmarked > 0 ? 'En las que no llevan marca no se registró ningún cobro.' : '',
    ].filter(Boolean).join(' ');
    return { detail, atRisk, flag, paid, paidFlag };
  }
  if (atRisk.size === 0) {
    // Sin ninguna venta con tarjeta sin resolver, la frase de siempre SÍ es cierta. Dice «no se
    // registró ningún cobro» y no «no se ha cobrado nada» (B0-04): lo primero es lo que la app sabe.
    return { detail: 'Continúa donde te quedaste o cancélalas; no se registró ningún cobro.', atRisk, flag, paid, paidFlag };
  }
  if (atRisk.size === 1) {
    return {
      detail: 'Una de estas ventas intentó cobrarse con el lector y puede tener dinero cobrado. Ábrela con «Continuar» para ver cómo quedó ese cobro. No la cobres otra vez ni la canceles antes de verlo.',
      atRisk,
      flag,
      paid,
      paidFlag,
    };
  }
  return {
    detail: 'Algunas de estas ventas intentaron cobrarse con el lector y pueden tener dinero cobrado. Ábrelas con «Continuar», una por una, para ver cómo quedó cada cobro. No las cobres otra vez ni las canceles antes de verlo.',
    atRisk,
    flag,
    paid,
    paidFlag,
  };
}

/**
 * P-017: el servidor entregó un intento NUEVO, pero el preflight (sesión / permisos) no deja abrir el
 * lector. El lector NO se abre y ese intento se cierra en el acto, honestamente: nada se cobró. Reportado
 * por el canal de recuperación, sin datos del SDK. El servidor lo deja CANCELED (sin evidencia de cobro).
 */
export const NOT_LAUNCHED_OBSERVATION: PaymentObservationInput = {
  source: 'RECOVERY',
  outcome: 'CANCELLED',
  rawReason: 'NotLaunched',
};

/**
 * ¿Se pide el intento SIN preflight? Solo cuando de verdad hay un cobro a medias que recuperar: el servidor
 * lo tiene en vilo (PROCESSING) o este equipo abrió el lector para esta venta y aún no reportó
 * ([readZettleLaunch]). En cualquier otro caso —incluido un cobro PENDING tras un intento cancelado— el
 * preflight corre y sus avisos de sesión y permisos se muestran.
 */
export function shouldSkipZettlePreflight(payments: ReadonlyArray<{ status: string }> | undefined, launchedAttemptId: string | undefined): boolean {
  return hasReaderPaymentInFlight(payments) || launchedAttemptId !== undefined;
}

/**
 * El orden ES la regla: primero el preflight y, SOLO si deja cobrar, el intento. Un preflight que
 * bloquea —o un puente que falla, que cuenta como "no disponible"— jamás llega a pedir un intento al
 * servidor. Un error al pedir el intento se propaga tal cual (el POS ya sabe tratarlo).
 *
 * `skipPreflight` (el servidor tiene un intento en vilo: cobro PROCESSING): se pide el intento directo,
 * porque el servidor devolverá el MISMO intento y el POS lo recuperará sin relanzar el SDK — la
 * recuperación nunca debe depender de la sesión del SDK ni de los permisos.
 *
 * `recoverEvenIfBlocked` (P-017: este equipo dejó una marca de lanzamiento para la venta): el preflight
 * corre igual, pero si bloquea NO impide pedir el intento —puede haber un cobro a medias que recuperar—;
 * solo impide abrir el lector: el resultado sale `started` con `blocked`, y quien llama decide: si el
 * servidor devolvió el MISMO intento, se recupera; si entregó uno NUEVO (la marca era vieja), se cierra sin
 * abrir nada ([NOT_LAUNCHED_OBSERVATION]). Sin marca ni intento en vilo, un bloqueo sigue sin pedir intento.
 * Salvedad (deuda): en OTRO equipo, o con la marca borrada, un intento SUBMITTED sin observación queda bajo
 * un cobro PENDING invisible para el cliente; ahí la recuperación espera a que el preflight pase (el
 * servidor sigue impidiendo cobrar de otra forma mientras haya un intento en vilo).
 */
export async function startZettleAttemptAfterPreflight<T>(
  current: ZettleAvailability,
  bridge: ZettlePreflightBridge,
  startAttempt: () => Promise<T>,
  options: { skipPreflight?: boolean; recoverEvenIfBlocked?: boolean } = {},
): Promise<ZettleAttemptStart<T>> {
  if (options.skipPreflight) return { kind: 'started', availability: current, start: await startAttempt() };
  let preflight: Awaited<ReturnType<typeof runZettlePreflight>>;
  try {
    preflight = await runZettlePreflight(current, bridge);
  } catch {
    if (options.recoverEvenIfBlocked) return { kind: 'started', availability: current, start: await startAttempt(), blocked: 'UNAVAILABLE' };
    return { kind: 'blocked', availability: current, reason: 'UNAVAILABLE' };
  }
  if (preflight.decision.step !== 'charge') {
    const reason: ZettlePreflightBlock = preflight.decision.step === 'blocked' ? preflight.decision.reason : 'UNAVAILABLE';
    if (options.recoverEvenIfBlocked) return { kind: 'started', availability: preflight.availability, start: await startAttempt(), blocked: reason };
    return { kind: 'blocked', availability: preflight.availability, reason };
  }
  return { kind: 'started', availability: preflight.availability, start: await startAttempt() };
}

/**
 * Qué ve el operador cuando el preflight no deja cobrar. Dice la verdad aunque la venta hubiera
 * dejado un cobro con lector en duda en una sesión anterior (proceso muerto): no se afirma "no se
 * cobró nada", sino que no se inició ningún cobro NUEVO, y se advierte no cobrar de otra forma.
 */
const PREFLIGHT_TRUTH = 'No se inició ningún cobro nuevo. Si esta venta ya tenía un cobro con lector en curso, no la cobres de otra forma.';

/**
 * P-017: cuando la venta tiene un cobro con tarjeta SIN CERRAR, el operador tiene que saber que la salida
 * es resolver lo que bloquea y volver a tocar con «Cobrar con lector Zettle», y no cobrar de otra forma mientras
 * no se sepa cómo quedó. No afirma que se haya cobrado: un cobro sin cerrar puede no haber cobrado nada.
 * Quien llama debe preguntarle al SERVIDOR si ese cobro existe (el último render puede ir atrasado).
 */
const PREFLIGHT_TRUTH_WITH_CARD = 'No se inició ningún cobro nuevo. Esta venta tiene un cobro con tarjeta sin cerrar: resuelve lo que falta y vuelve a tocar «Confirmar cobro» con «Cobrar con lector Zettle»; no la cobres de otra forma hasta saber cómo quedó.';

export function describeZettlePreflightBlock(reason: ZettlePreflightBlock, options: { readerPaymentPending?: boolean } = {}): ZettleNotice {
  const notice = describePreflightBlockBase(reason);
  if (!options.readerPaymentPending) return notice;
  return { ...notice, cardPending: true, detail: notice.detail.replace(PREFLIGHT_TRUTH, PREFLIGHT_TRUTH_WITH_CARD) };
}

function describePreflightBlockBase(reason: ZettlePreflightBlock): ZettleNotice {
  switch (reason) {
    case 'LOGIN_REQUIRED':
      return {
        tone: 'warning',
        title: 'Falta iniciar sesión en Zettle',
        detail: `Este build piloto cobra dinero real con la cuenta Zettle del negocio. Inicia sesión y vuelve a tocar «Confirmar cobro». ${PREFLIGHT_TRUTH}`,
        action: 'login',
        topic: 'zettle-login',
      };
    case 'PERMISSIONS_DENIED':
      return {
        tone: 'warning',
        title: 'El lector necesita permisos',
        detail: `Android pide la ubicación precisa (y, en equipos recientes, «Dispositivos cercanos») para encontrar el lector. Vuelve a tocar «Confirmar cobro» y acepta. ${PREFLIGHT_TRUTH}`,
        topic: 'zettle-reader-permissions',
      };
    case 'PERMISSIONS_DENIED_PERMANENTLY':
      return {
        tone: 'error',
        title: 'Permisos del lector sin conceder',
        detail: `Vuelve a tocar «Confirmar cobro»; si Android ya no muestra el aviso, toca «Abrir ajustes» (o ve a Ajustes › Aplicaciones › esta app) y en Permisos activa Ubicación precisa y, si aparece, Dispositivos cercanos. No hace falta reinstalar la app. ${PREFLIGHT_TRUTH}`,
        action: 'app-settings',
        topic: 'zettle-reader-permissions',
      };
    default:
      return {
        tone: 'error',
        title: 'El cobro con lector no está disponible',
        detail: `Este build no tiene el lector disponible. ${PREFLIGHT_TRUTH}`,
      };
  }
}

// ─── P-015 · la pantalla activa converge a la verdad del servidor ─────────────────────────────────

/** Aviso tras abrir el inicio de sesión del SDK. `topic` permite reemplazarlo cuando la sesión ya existe. */
export const ZETTLE_LOGIN_OPENED_NOTICE: ZettleNotice = {
  tone: 'info',
  title: 'Inicio de sesión de Zettle abierto',
  detail: 'Complétalo; al terminar la app vuelve sola a esta pantalla. No se inició ningún cobro nuevo.',
  topic: 'zettle-login',
};

/**
 * Qué aviso queda después de volver a consultar la sesión del SDK (al regresar a la app o cuando el
 * lado nativo avisa que la sesión cambió). Solo se toca un aviso QUE HABLABA DEL LOGIN, y solo si el
 * PUENTE —no el evento— dice que ya hay sesión: jamás tapa un cobro en duda ni inicia un cobro.
 */
export function noticeAfterZettleSessionCheck(notice: ZettleNotice | undefined, availability: ZettleAvailability): ZettleNotice | undefined {
  if (notice?.topic !== 'zettle-login') return notice;
  if (availability.mode !== 'REAL' || availability.loggedIn !== true) return notice;
  // Iniciar sesión no resuelve un cobro con tarjeta sin cerrar: el hecho lo dijo el SERVIDOR y no
  // puede borrarse porque cambió otra cosa. Sin esto, un aviso rojo se volvía verde con la venta
  // todavía en duda, y el backend SÍ acepta cobrar en efectivo un cobro con tarjeta en PENDING.
  if (notice.cardPending) {
    return {
      tone: 'warning',
      title: 'Sesión iniciada · la venta sigue con un cobro sin cerrar',
      detail: PREFLIGHT_TRUTH_WITH_CARD,
      cardPending: true,
    };
  }
  return {
    tone: 'success',
    title: 'Sesión de Zettle iniciada',
    detail: 'Ya puedes tocar «Confirmar cobro». No se inició ningún cobro nuevo.',
  };
}

/**
 * Deuda (l): qué aviso queda después de volver a CONSULTAR los permisos del lector (al regresar de
 * los Ajustes de Android). Misma forma que la comprobación de sesión: solo toca un aviso que hablaba
 * de permisos, y solo si el puente lo confirma.
 *
 * Un aviso con `cardPending` NO se degrada a «todo en orden»: ese hecho lo dijo el servidor y sigue
 * siendo cierto aunque Android ya conceda el permiso.
 *
 * Falla cerrado: `undefined` —no se pudo consultar— deja el aviso rojo como estaba.
 */
export function noticeAfterZettleReaderPermissionCheck(
  notice: ZettleNotice | undefined,
  availability: ZettleAvailability,
  status: ZettleReaderPermissionStatus | undefined,
): ZettleNotice | undefined {
  if (notice?.topic !== 'zettle-reader-permissions') return notice;
  // En un build DEV el plugin responde GRANTED por vacío: no hay permisos que exigir.
  if (availability.mode !== 'REAL') return notice;
  if (status !== 'GRANTED') return notice;
  if (notice.cardPending) {
    return {
      tone: 'warning',
      title: 'Permisos concedidos · la venta sigue con un cobro sin cerrar',
      detail: PREFLIGHT_TRUTH_WITH_CARD,
      cardPending: true,
    };
  }
  return {
    tone: 'success',
    title: 'Permisos del lector concedidos',
    detail: `Ya puedes tocar «Confirmar cobro» con «Cobrar con lector Zettle». ${PREFLIGHT_TRUTH}`,
  };
}

/**
 * Une los disparos simultáneos (la app vuelve al frente Y el SDK avisa el cambio de sesión) en UNA
 * sola consulta al puente a la vez. Un disparo que llega con una consulta EN CURSO no se pierde: esa
 * consulta pudo leer el estado anterior, así que al terminar se hace UNA más (nunca una ráfaga).
 * Solo consulta: no existe camino desde aquí hacia `login()`.
 */
export function createZettleSessionRefresher(
  check: () => Promise<ZettleAvailability>,
  apply: (availability: ZettleAvailability) => void,
): () => Promise<void> {
  let inFlight: Promise<void> | undefined;
  let again = false;
  const run = (): Promise<void> => check()
    .then(apply)
    .catch(() => undefined)
    .then(() => {
      if (!again) return undefined;
      again = false;
      return run();
    });
  return () => {
    if (inFlight) { again = true; return inFlight; }
    inFlight = run().finally(() => { inFlight = undefined; });
    return inFlight;
  };
}

/**
 * Lo que la venta —tal como la entrega `GET /api/sales/:id`, de SOLO LECTURA— dice del cobro con lector:
 *  · confirmed — el servidor ya la completó (p. ej. la reconciliación confirmó el cobro);
 *  · in-doubt  — el servidor aún tiene un intento EN VILO: el cobro está en PROCESSING (así lo deriva
 *                `derivePaymentStatus` mientras haya un intento enviado, procesando o en duda — también si
 *                la reconciliación lo dejó para revisión manual). Nunca se lee como fallo;
 *  · paid      — FI-53: la venta sigue por cobrar y el servidor YA confirmó su cobro (la venta no se pudo
 *                completar). Tiene veredicto propio porque el vigilante solo avisa cuando el veredicto
 *                CAMBIA: leído como 'settled', una lectura «sin cobrar» seguida de una «cobro confirmado»
 *                no avisaba nada (revisión independiente de P-067). No es terminal: la venta todavía puede
 *                completarse y pasar a 'confirmed';
 *  · settled   — el servidor ya no tiene ningún intento en vilo y la venta no está completada (el intento
 *                terminó sin éxito y el cobro volvió a PENDING, o la venta se cerró por otro camino). Aquí
 *                NO se interpreta ni se libera nada: el desenlace lo entrega «Verificar cobro».
 */
export type ReaderSaleTruth = 'confirmed' | 'paid' | 'in-doubt' | 'settled';

export function readReaderSaleTruth(sale: { status: string; payments?: ReadonlyArray<{ status: string }> }): ReaderSaleTruth {
  if (sale.status === 'CONFIRMED') return 'confirmed';
  if (sale.status === 'AWAITING_PAYMENT' && hasConfirmedPayment(sale.payments)) return 'paid';
  if (sale.status === 'AWAITING_PAYMENT' && (sale.payments ?? []).some((payment) => payment.status === 'PROCESSING')) return 'in-doubt';
  return 'settled';
}

/**
 * ESCALERA de esperas entre consultas; su último valor se repite hasta que vence la ventana.
 *
 * Deuda (s): antes esto ERA la ventana — 11 turnos, exactamente 300 000 ms — y ahí estaba el defecto.
 * La reconciliación del servidor reintenta a 1, 5, 15, 30 y 60 minutos, así que sus consultas caen
 * hacia los 2, 7, 22, 52 y 112 minutos. Una ventana de 5 minutos **se rendía justo antes de la
 * segunda consulta del servidor**: el peor número posible. El 2026-09-29 eso obligó a cerrar y
 * reabrir la app para ver una venta que ya estaba cobrada.
 */
export const READER_SALE_REFRESH_DELAYS_MS: readonly number[] = [4_000, 6_000, 10_000, 10_000, 15_000, 15_000, 30_000, 30_000, 60_000, 60_000, 60_000];
/**
 * Ventana de RELOJ, no de turnos: cubre la 1.ª, 2.ª y 3.ª consulta del servidor con holgura. No se
 * eligen 55 minutos (cubrir la 4.ª) porque a los 25 el cliente ya se fue y el caso dejó de ser una
 * operación de mostrador; mantener un teléfono consultando una hora gasta batería y datos.
 */
export const READER_SALE_WATCH_WINDOW_MS = 25 * 60_000;
/** Cada regreso al frente con la venta todavía en duda estira la ventana esto, nunca el propio vigilante. */
export const READER_SALE_WATCH_EXTENSION_MS = 10 * 60_000;
/** Techo absoluto desde el arranque: un vigilante infinito no se acepta. */
export const READER_SALE_WATCH_MAX_MS = 90 * 60_000;
/** Separación mínima entre dos lecturas, venga de donde venga el disparo (evita ráfagas al volver a la app). */
export const READER_SALE_REFRESH_MIN_GAP_MS = 3_000;
/** Una lectura sin respuesta en este tiempo cuenta como error de red (una red móvil a medias no cuelga al vigilante). */
export const READER_SALE_FETCH_TIMEOUT_MS = 15_000;

export const READER_SALE_STILL_IN_DOUBT_NOTICE: ZettleNotice = {
  tone: 'warning',
  title: 'El cobro sigue en verificación',
  detail: 'El servidor todavía no confirma este cobro. No vuelvas a cobrar ni cobres de otra forma. Toca «Verificar cobro» o revisa la venta en el historial; si sigue así, avisa al encargado para revisarla.',
};

export const READER_SALE_SETTLED_NOTICE: ZettleNotice = {
  tone: 'warning',
  title: 'El servidor ya cerró este cobro',
  detail: 'Toca «Verificar cobro» para ver el resultado antes de hacer cualquier otra cosa. No vuelvas a cobrar todavía.',
};

/**
 * Deuda (q): lo que ve la persona al retomar una venta guardada, según lo que diga el SERVIDOR.
 *
 * La decisión del dueño manda sobre estos textos: **el encargado no puede consultar la cuenta de
 * Zettle en el momento**, así que ninguno puede pedir una verificación inmediata ni prometer una
 * respuesta que nadie va a dar en el turno. La acción principal es siempre la misma: dejarla
 * pendiente y seguir vendiendo.
 */
export const READER_SALE_IN_DOUBT_NOTICE: ZettleNotice = {
  tone: 'warning',
  title: 'Estamos confirmando este cobro',
  detail: 'Esta venta tiene un cobro con tarjeta que el servidor todavía está confirmando. No la cobres de otra forma ni la canceles. Puedes dejarla pendiente y seguir atendiendo: se resuelve sola o la revisa el dueño.',
};

/**
 * Este aviso AVISA, no bloquea. Casi siempre corresponde a algo que el servidor sabe limpio —un
 * rechazo determinante o un lector que nunca se abrió—, así que prohibir cobrar aquí rompería la
 * operación normal y contradiría al propio servidor, que dice «no se leyó ninguna tarjeta, cobra en
 * efectivo». Lo que sí se pide es volver a elegir el método, para que nadie confirme por inercia.
 */
export const READER_SALE_CARD_UNRESOLVED_NOTICE: ZettleNotice = {
  tone: 'warning',
  title: 'Esta venta ya pasó por el lector',
  detail: 'Se intentó cobrar con tarjeta y no salió. Puedes volver a intentarlo con el lector o cobrar de otra forma, pero elige tú cómo te pagaron: no lo damos por hecho. Si crees que al cliente sí se le cobró, déjala pendiente y avisa al dueño.',
};

/**
 * FI-53: lo que ve la persona con el cobro confirmado y la venta sin completar. Dice SOLO lo que se
 * sabe: el cobro está confirmado (lo dijo el servidor) y la venta todavía no. No dice «venta
 * completada», no dice que no se cobró, y no ofrece cobrar de nuevo: la única acción es completar la
 * venta, que no cobra.
 */
export const READER_SALE_PAID_NOTICE: ZettleNotice = {
  tone: 'warning',
  title: 'Cobro confirmado. Falta completar la venta',
  detail: 'El cobro con tarjeta de esta venta ya quedó confirmado: no vuelvas a cobrar ni cobres de otra forma. La venta todavía no se completa. Toca «Completar la venta»; si no se puede, déjala pendiente y avisa al encargado.',
};

/**
 * El servidor confirmó el cobro y la venta NO se pudo leer: de ella no se afirma nada, ni que quedó
 * completada (lo que la pantalla daba por hecho antes) ni que no.
 */
export const READER_SALE_PAID_UNREAD_NOTICE: ZettleNotice = {
  tone: 'warning',
  title: 'Cobro confirmado',
  detail: 'El servidor confirmó el cobro con tarjeta: no vuelvas a cobrar ni cobres de otra forma. No pudimos leer si la venta ya quedó completada. Toca «Completar la venta»: no cobra de nuevo.',
};

/**
 * El servidor confirmó el cobro de una venta que ya estaba cancelada o anulada (una anomalía: por
 * ejemplo, se cerró por declaración y después el lector dijo que sí había cobrado). No hay nada que
 * completar; lo que no se puede es decir «no se registró ningún cobro».
 */
export function describePaidSaleClosed(status: string): string {
  const how = status === 'CANCELED' ? 'cancelada' : 'anulada';
  return `El servidor confirmó el cobro con tarjeta, pero esta venta ya estaba ${how}. No vuelvas a cobrar al cliente. Avisa al dueño: hay que revisarla en el Historial.`;
}

export interface ReaderSaleWatcherDeps<S extends { status: string; payments?: ReadonlyArray<{ status: string }> }> {
  /** ÚNICA llamada de red del vigilante: una LECTURA de la venta. Jamás un intento, observación ni reconciliación. */
  fetchSale: () => Promise<S>;
  onConfirmed: (sale: S) => void;
  /**
   * Deuda (s): se dispara solo cuando el veredicto CAMBIA, porque ya no detiene al vigilante. También
   * con 'paid' (FI-53): ya no hay nada en vilo, y quien llama decide qué hacer con lo que trae la venta.
   */
  onSettled: (sale: S) => void;
  /** Deuda (s): el servidor sigue en duda. También solo en el cambio de veredicto. */
  onInDoubt?: (sale: S) => void;
  /**
   * Deuda (s): 0 = el servidor respondió; N = N lecturas seguidas sin respuesta. La pantalla no puede
   * seguir prometiendo que pregunta sola si no está logrando preguntar.
   */
  onReachability?: (consecutiveFailures: number) => void;
  /** Se acabó la ventana de consultas y la venta sigue en duda. */
  onExhausted: () => void;
  /** false = la app está en segundo plano: no se consulta y la ventana se pausa hasta `checkNow`. */
  isVisible?: () => boolean;
  /**
   * true = la pantalla está en otra operación (p. ej. «Verificar cobro»): ese turno no lee, y una lectura
   * que ya iba en camino se DESCARTA al volver — esa operación es la que decide qué ve el operador.
   */
  isBusy?: () => boolean;
  fetchTimeoutMs?: number;
  now?: () => number;
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  delaysMs?: readonly number[];
  /** Solo para las pruebas: probar 25 minutos sin esperar 25 minutos. */
  windowMs?: number;
  extensionMs?: number;
  maxMs?: number;
}

export interface ReaderSaleWatcher {
  /** La app volvió al frente: una lectura ahora (respetando la separación mínima). */
  checkNow: () => void;
  stop: () => void;
}

/**
 * Vigila una venta con un cobro con lector EN DUDA hasta que el servidor diga algo definitivo. Solo lee.
 * Acotado: sigue [READER_SALE_REFRESH_DELAYS_MS] y se detiene; en segundo plano no consulta (se reanuda
 * con `checkNow` al volver). Un error de red no es evidencia de nada: la venta sigue en duda.
 */
export function watchReaderSale<S extends { status: string; payments?: ReadonlyArray<{ status: string }> }>(deps: ReaderSaleWatcherDeps<S>): ReaderSaleWatcher {
  const delays = deps.delaysMs ?? READER_SALE_REFRESH_DELAYS_MS;
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((run, ms) => setTimeout(run, ms));
  const clearTimer = deps.clearTimer ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const isVisible = deps.isVisible ?? (() => true);
  const isBusy = deps.isBusy ?? (() => false);
  const fetchTimeoutMs = deps.fetchTimeoutMs ?? READER_SALE_FETCH_TIMEOUT_MS;
  let stopped = false;
  let inFlight = false;
  let step = 0;
  let timer: unknown;
  let lastCheckAt = Number.NEGATIVE_INFINITY;
  // Deuda (s): el tope es el RELOJ, no los turnos. Así una racha sin red ya no acorta la ventana.
  const startedAt = now();
  const extensionMs = deps.extensionMs ?? READER_SALE_WATCH_EXTENSION_MS;
  const maxMs = deps.maxMs ?? READER_SALE_WATCH_MAX_MS;
  let deadline = startedAt + (deps.windowMs ?? READER_SALE_WATCH_WINDOW_MS);
  let failures = 0;
  let lastTruth: ReaderSaleTruth | undefined;

  const schedule = () => {
    if (stopped || timer !== undefined) return;
    if (now() >= deadline) return;
    timer = setTimer(() => { timer = undefined; void check(true); }, delays[Math.min(step, delays.length - 1)]);
  };

  const check = async (scheduled: boolean) => {
    if (stopped || inFlight) return;
    // En segundo plano no se consulta ni se rearma ningún temporizador: `checkNow` reanuda al volver.
    if (!isVisible()) return;
    if (!scheduled && now() - lastCheckAt < READER_SALE_REFRESH_MIN_GAP_MS) { schedule(); return; }
    // Pantalla ocupada: este turno no lee ni gasta la ventana; se reintenta con la misma espera.
    if (isBusy()) { schedule(); return; }
    let truth: ReaderSaleTruth = 'in-doubt';
    let sale: S | undefined;
    inFlight = true;
    lastCheckAt = now();
    try {
      sale = await withTimeout(deps.fetchSale());
      truth = readReaderSaleTruth(sale);
    } catch {
      // Sin respuesta (o sin respuesta a tiempo) ≠ fallo del cobro.
    } finally {
      inFlight = false;
    }
    if (stopped) return;
    // Mientras se leía, la pantalla empezó otra operación: lo leído se descarta y se reintenta después.
    if (isBusy()) { schedule(); return; }
    failures = sale ? 0 : failures + 1;
    deps.onReachability?.(failures);
    // 'confirmed' es el ÚNICO veredicto terminal: el servidor confirmó el dinero, no hay nada más que
    // vigilar. Deuda (s): antes 'settled' también detenía al vigilante, y un vigilante detenido ignora
    // `checkNow` para siempre — ni volver al frente podía rescatar la pantalla. Pero 'settled' no es
    // terminal para el dinero: una reconciliación manual o un segundo intento pueden llevar la venta a
    // CONFIRMED después.
    if (sale && truth === 'confirmed') { stop(); deps.onConfirmed(sale); return; }
    if (sale && truth !== lastTruth) {
      if (truth === 'settled' || truth === 'paid') deps.onSettled(sale);
      else deps.onInDoubt?.(sale);
      lastTruth = truth;
    }
    // Un regreso al frente con la venta todavía en duda estira la ventana, con techo desde el arranque.
    // Solo lo dispara un evento humano o del sistema (`checkNow`), nunca el propio vigilante.
    if (!scheduled && sale) deadline = Math.min(startedAt + maxMs, Math.max(deadline, now() + extensionMs));
    if (!scheduled) { schedule(); return; }
    // Deuda (s): el turno solo avanza con respuesta REAL. Una lectura fallida ya no gasta ventana.
    if (sale) step += 1;
    if (now() >= deadline) deps.onExhausted();
    else schedule();
  };

  const withTimeout = (pending: Promise<S>) => new Promise<S>((resolve, reject) => {
    const handle = setTimer(() => reject(new Error('READER_SALE_FETCH_TIMEOUT')), fetchTimeoutMs);
    pending.then(
      (value) => { clearTimer(handle); resolve(value); },
      (error) => { clearTimer(handle); reject(error); },
    );
  });

  const stop = () => {
    stopped = true;
    if (timer !== undefined) { clearTimer(timer); timer = undefined; }
  };

  schedule();
  return { checkNow: () => { void check(false); }, stop };
}
